/**
 * Free Shipping Bar Web Component
 */

// Header bubble updater - exposed globally so the cart bar component can also trigger updates
window.updateFreeShippingBubble = (function() {
  let bubble, threshold, maxWeight, textEl;

  let achievedText, progressText;

  function init() {
    bubble = document.getElementById('free-shipping-bubble');
    if (!bubble) return false;
    threshold = parseInt(bubble.dataset.threshold, 10) || 0;
    maxWeight = parseInt(bubble.dataset.maxWeight, 10) || 0;
    achievedText = bubble.dataset.achievedText || 'Fri frakt!';
    progressText = bubble.dataset.progressText || '{{ remaining }} till fri frakt';
    textEl = bubble.querySelector('.free-shipping-bubble__text');
    return true;
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function updateBubble(cart) {
    if (!bubble && !init()) return;

    const hasItems = cart.item_count > 0;
    const weightExceeded = maxWeight > 0 && cart.total_weight > maxWeight;
    const achieved = hasItems && cart.total_price >= threshold && !weightExceeded;
    const remaining = Math.max(0, threshold - cart.total_price);

    // Only toggle classes if they actually need to change to prevent animation restart
    const shouldBeVisible = hasItems && !weightExceeded;
    if (bubble.classList.contains('free-shipping-bubble--visible') !== shouldBeVisible) {
      bubble.classList.toggle('free-shipping-bubble--visible', shouldBeVisible);
    }
    if (bubble.classList.contains('free-shipping-bubble--achieved') !== achieved) {
      bubble.classList.toggle('free-shipping-bubble--achieved', achieved);
    }

    if (textEl) {
      if (achieved) {
        textEl.textContent = achievedText;
      } else if (hasItems && !weightExceeded) {
        // Replace placeholders in progress text from settings
        const formattedRemaining = formatMoney(remaining);
        const formattedThreshold = formatMoney(threshold);
        let message = progressText
          .replace('{{ remaining }}', formattedRemaining)
          .replace('{{ threshold }}', formattedThreshold);
        textEl.textContent = message;
      }
    }
  }

  // Initialize and subscribe
  if (init()) {
    // Wait for pubsub to be available, then subscribe
    function trySubscribe() {
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.cart) {
            updateBubble(event.cart);
          } else {
            fetch('/cart.js')
              .then(r => r.json())
              .then(updateBubble)
              .catch(() => {});
          }
        });
      } else {
        setTimeout(trySubscribe, 100);
      }
    }
    trySubscribe();
  }

  return updateBubble;
})();

if (!customElements.get('free-shipping-bar')) {
  class FreeShippingBar extends HTMLElement {
    constructor() {
      super();
      this.threshold = parseInt(this.dataset.threshold, 10) || 0;
      this.maxWeight = parseInt(this.dataset.maxWeight, 10) || 0;
      this.progressText = this.dataset.progressText || '{{ remaining }} kvar till fri frakt';
      this.achievedText = this.dataset.achievedText || 'Grattis! Du har fri frakt!';
      this.weightExceededText = this.dataset.weightExceededText || 'Fri frakt gäller ej för tunga beställningar';
      this.pendingUpdate = false;
      this.debounceTimer = null;
    }

    cartUpdateUnsubscriber = undefined;

    connectedCallback() {
      try {
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
        }
      } catch (e) {
        console.warn('Free shipping bar: Could not subscribe to cart updates', e);
      }
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
    }

    onCartUpdate(event) {
      // If the event includes cart data, use it directly instead of fetching
      if (event && event.cart) {
        this.updateProgress(event.cart);
        return;
      }

      // Debounce to prevent too many requests
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      if (this.pendingUpdate) {
        return;
      }

      this.debounceTimer = setTimeout(() => {
        this.pendingUpdate = true;
        fetch('/cart.js')
          .then((response) => response.json())
          .then((cart) => {
            this.updateProgress(cart);
          })
          .catch((error) => {
            console.warn('Free shipping bar: Failed to fetch cart', error);
          })
          .finally(() => {
            this.pendingUpdate = false;
          });
      }, 300);
    }

    updateProgress(cart) {
      try {
        const cartTotal = cart.total_price;
        const cartWeight = cart.total_weight;
        const remaining = this.threshold - cartTotal;
        const progressPercent = this.threshold > 0 ? Math.min((cartTotal / this.threshold) * 100, 100) : 0;
        const weightExceeded = this.maxWeight > 0 && cartWeight > this.maxWeight;
        const achieved = remaining <= 0 && !weightExceeded;

        // Hide entire component if weight exceeded
        if (weightExceeded) {
          this.style.display = 'none';
          // Still update header bubble
          if (typeof window.updateFreeShippingBubble === 'function') {
            window.updateFreeShippingBubble(cart);
          }
          return;
        }
        this.style.display = '';

        // Only toggle class if state actually changed to prevent animation restart
        if (this.classList.contains('free-shipping-bar--achieved') !== achieved) {
          this.classList.toggle('free-shipping-bar--achieved', achieved);
        }

        const messageEl = this.querySelector('.free-shipping-bar__message');
        if (messageEl) {
          if (achieved) {
            messageEl.innerHTML = this.achievedText;
          } else {
            const formattedRemaining = this.formatMoney(remaining);
            const formattedThreshold = this.formatMoney(this.threshold);
            // Replace placeholders in the progress text from settings
            let progressMessage = this.progressText
              .replace('{{ remaining }}', `<span class="free-shipping-bar__remaining">${formattedRemaining}</span>`)
              .replace('{{ threshold }}', formattedThreshold);
            messageEl.innerHTML = progressMessage;
          }
        }

        // Hide threshold label when achieved
        const thresholdLabel = this.querySelector('.free-shipping-bar__threshold-label');
        if (thresholdLabel) {
          thresholdLabel.style.display = achieved ? 'none' : '';
        }

        const progressFill = this.querySelector('.free-shipping-bar__fill');
        if (progressFill) {
          progressFill.style.width = `${progressPercent}%`;
        }

        // Also update header bubble
        if (typeof window.updateFreeShippingBubble === 'function') {
          window.updateFreeShippingBubble(cart);
        }
      } catch (e) {
        console.warn('Free shipping bar: Error updating progress', e);
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat);
      }
      return `${amount.replace('.', ',')} €`;
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
}
